import * as Sentry from '@sentry/react';
import { sanitizeLogContext } from './sanitizeLogContext';

type LogContext = Record<string, unknown>;

function sanitize(context: LogContext): LogContext {
  return sanitizeLogContext(context);
}

export function captureException(error: unknown, context?: LogContext): void {
  if (context && Object.keys(context).length > 0) {
    Sentry.withScope(scope => {
      const clean = sanitize(context);
      for (const [key, value] of Object.entries(clean)) {
        scope.setExtra(key, value);
      }
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

type SentryLevel = 'debug' | 'info' | 'warning' | 'error';

function logMessage(level: SentryLevel, message: string, context?: LogContext): void {
  if (import.meta.env.DEV) {
    const consoleFn = level === 'warning' ? console.warn : level === 'error' ? console.error : level === 'debug' ? console.debug : console.info;
    consoleFn(message, context ?? '');
  }
  Sentry.withScope(scope => {
    scope.setLevel(level);
    if (context && Object.keys(context).length > 0) {
      const clean = sanitize(context);
      for (const [key, value] of Object.entries(clean)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureMessage(message);
  });
}

export function logDebug(message: string, context?: LogContext): void {
  logMessage('debug', message, context);
}

export function logInfo(message: string, context?: LogContext): void {
  logMessage('info', message, context);
}

export function logWarning(message: string, context?: LogContext): void {
  logMessage('warning', message, context);
}

export function logError(message: string, context?: LogContext): void {
  logMessage('error', message, context);
}

export function addBreadcrumb(message: string, context?: LogContext): void {
  Sentry.addBreadcrumb({
    message,
    data: context ? sanitize(context) : undefined,
  });
}

export function setUser(user: { id?: string | number | null } | null): void {
  if (user?.id) {
    Sentry.setUser({ id: String(user.id) });
  } else {
    Sentry.setUser(null);
  }
}
